import React, {useEffect, useState, useMemo} from "react";
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    DatePicker,
    Typography,
    message,
    Tabs,
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import {useNavigate} from "react-router-dom";

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

const {Title} = Typography;

interface TopPlayerEntry {
    id: number;
    mmid: string;
    theo: number;
    customer?: {
        id: number;
        first_name: string;
        last_name: string;
        email?: string;
    };
    period_start: string;
    period_end: string;
}

const tabItems = [
    {key: "this_week", label: "This Week"},
    {key: "last_week", label: "Last Week"},
    {key: "this_month", label: "This Month"},
    {key: "last_month", label: "Last Month"},
    {key: "this_quarter", label: "This Quarter"},
    {key: "last_quarter", label: "Last Quarter"},
    {key: "this_year", label: "This Year"},
    {key: "last_year", label: "Last Year"},
];

const TopPlayers: React.FC = () => {
    const [players, setPlayers] = useState<TopPlayerEntry[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const {Title, Text} = Typography;
    const [activeTab, setActiveTab] = useState("this_week");

    const fetchPlayers = async () => {
        setLoading(true);
        try {
            const res = await api.get("/cc/top-players/", {params: {all: true}});
            setPlayers(Array.isArray(res.data) ? res.data : res.data.results || []);
        } catch (err: any) {
            if (err.response?.status === 401) {
                message.error("Unauthorized – please log in again.");
            } else {
                message.error("Failed to load top players");
            }
            setPlayers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlayers();
    }, []);

    const handleSubmit = async (values: any) => {
        try {
            const period_start = values.period[0].format("YYYY-MM-DD");
            const period_end = values.period[1].format("YYYY-MM-DD");

            const payload = {
                period_start,
                period_end,
                entries: values.entries.map((entry: any) => ({
                    mmid: entry.mmid,
                    theo: parseInt(entry.theo, 10),
                })),
            };

            await api.post("/cc/top-players/bulk-update/", payload);

            message.success("Top players saved");
            setModalOpen(false);
            setEditingPeriod(null);
            form.resetFields();
            fetchPlayers();
        } catch (err: any) {
            console.error(err.response?.data || err);
            if (err.response?.data?.mmid) {
                message.error(`MMID error: ${err.response.data.mmid.join(", ")}`);
            } else {
                message.error("Failed to save top players");
            }
        }
    };

    const columns = [
        {
            title: "Customer",
            dataIndex: "customer",
            render: (c: any) =>
                c ? `${c.first_name} ${c.last_name}` : "N/A",
        },
        {title: "MMID", dataIndex: "mmid"},
        {
            title: "Email",
            dataIndex: ["customer", "email"],
            render: (email: string) => email || "N/A",
        },
        {
            title: "Theo",
            dataIndex: "theo",
            render: (value: number | string) => {
                const intValue = Math.floor(Number(value));
                return intValue.toLocaleString("en-US");
            },
        },
        {
            title: "Actions",
            render: (_: any, rec: TopPlayerEntry) => (
                <Button
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(rec.period_start, rec.period_end);
                    }}
                >
                    Edit
                </Button>
            ),
        },
    ];

    // Utility: determine default ranges
    const getDefaultRange = (tabKey: string) => {
        const now = dayjs();
        switch (tabKey) {
            case "this_week":
                return [now.startOf("week"), now.endOf("week")];
            case "last_week":
                return [
                    now.subtract(1, "week").startOf("week"),
                    now.subtract(1, "week").endOf("week"),
                ];
            case "this_month":
                return [now.startOf("month"), now.endOf("month")];
            case "last_month":
                return [
                    now.subtract(1, "month").startOf("month"),
                    now.subtract(1, "month").endOf("month"),
                ];
            case "this_quarter":
                return [now.startOf("quarter"), now.endOf("quarter")];
            case "last_quarter":
                return [
                    now.subtract(1, "quarter").startOf("quarter"),
                    now.subtract(1, "quarter").endOf("quarter"),
                ];
            case "this_year":
                return [now.startOf("year"), now.endOf("year")];
            case "last_year":
                return [now.subtract(1, "year").startOf("year"), now.subtract(1, "year").endOf("year")];
            default:
                return [now.startOf("week"), now.endOf("week")];
        }
    };

    const formatRangeLabel = (tabKey: string) => {
        const now = dayjs();

        const fmtMD = (d: dayjs.Dayjs) => d.format("M/D");
        const fmtMY = (d: dayjs.Dayjs) => d.format("MM/YYYY");
        const fmtY = (d: dayjs.Dayjs) => d.format("YYYY");

        switch (tabKey) {
            case "this_week": {
                const start = now.startOf("week");
                const end = now.endOf("week");
                return `${fmtMD(start)} ~ ${fmtMD(end)}`;
            }
            case "last_week": {
                const base = now.subtract(1, "week");
                const start = base.startOf("week");
                const end = base.endOf("week");
                return `${fmtMD(start)} ~ ${fmtMD(end)}`;
            }
            case "this_month":
                return now.format("MMMM"); // e.g., September
            case "last_month":
                return now.subtract(1, "month").format("MMMM");
            case "this_quarter": {
                const start = now.startOf("quarter");
                const end = now.endOf("quarter");
                return `${fmtMY(start)} ~ ${fmtMY(end)}`;
            }
            case "last_quarter": {
                const base = now.subtract(1, "quarter");
                const start = base.startOf("quarter");
                const end = base.endOf("quarter");
                return `${fmtMY(start)} ~ ${fmtMY(end)}`;
            }
            case "this_year":
                return fmtY(now);
            case "last_year":
                return fmtY(now.subtract(1, "year"));
            default:
                return "";
        }
    };

    const rangeLabel = useMemo(() => formatRangeLabel(activeTab), [activeTab]);


    // Filter by active tab
    const filteredPlayers = useMemo(() => {
        if (players.length === 0) return [];


        // Step 1: Filter relevant entries by overlap with tab range
        const [start, end] = getDefaultRange(activeTab);

        const relevant = players.filter(
            (p) =>
                dayjs(p.period_end).isSameOrAfter(start, "day") &&
                dayjs(p.period_start).isSameOrBefore(end, "day")
        );

        // Step 2: Group by MMID and sum theo
        const aggregated: Record<string, TopPlayerEntry> = {};

        for (const entry of relevant) {
            const theoValue = Number(entry.theo) || 0;

            if (!aggregated[entry.mmid]) {
                aggregated[entry.mmid] = {
                    ...entry,
                    theo: 0,
                    // ensure one customer reference per MMID
                    customer: entry.customer,
                };
            }

            aggregated[entry.mmid].theo += theoValue;
        }

        // Step 3: Convert back to array and sort high → low theo
        return Object.values(aggregated).sort((a, b) => b.theo - a.theo);
    }, [players, activeTab]);

    const openEditModal = (periodStart: string, periodEnd: string) => {
        setEditingPeriod(periodStart);
        const existing = players.filter(p => p.period_start === periodStart && p.period_end === periodEnd);
        form.setFieldsValue({
            period: [dayjs(periodStart), dayjs(periodEnd)],
            entries: existing.map(p => ({mmid: p.mmid, theo: p.theo})),
        });
        setModalOpen(true);
    };
    return (
        <Card
            title={
                <div style={{display: "flex", alignItems: "baseline", gap: 12}}>
                    <Title level={3} style={{margin: 0}}>Top Players</Title>
                    <Text type="secondary" style={{fontSize: 14}}>{rangeLabel}</Text>
                </div>
            }
            extra={
                <Button
                    type="primary"
                    onClick={() => {
                        setEditingPeriod(null);
                        form.setFieldsValue({
                            period: getDefaultRange(activeTab),
                            entries: Array.from({length: 10}, () => ({mmid: "", theo: 0})),
                        });
                        setModalOpen(true);
                    }}
                >
                    Update
                </Button>
            }
        >
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={tabItems}
            />

            <Table
                rowKey="id"
                dataSource={filteredPlayers}
                columns={columns}
                loading={loading}
                locale={{emptyText: "No Data Available"}}
                onRow={(record) => ({
                    onClick: () => navigate(`/cc/customers/${record.customer?.id}`),
                    style: {cursor: "pointer"},
                })}
            />

            <Modal
                title={editingPeriod ? "Edit Top 10 Players" : "Update Top 10 Players"}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="period" label="Period" rules={[{required: true}]}>
                        <DatePicker.RangePicker/>
                    </Form.Item>

                    <Form.List
                        name="entries"
                        rules={[
                            {
                                validator: async (_, entries) => {
                                    if (!entries || entries.length !== 10) {
                                        return Promise.reject(new Error("Exactly 10 players required"));
                                    }
                                },
                            },
                        ]}
                    >
                        {(fields) => (
                            <>
                                {fields.map((field) => (
                                    <div
                                        key={field.key}
                                        style={{display: "flex", gap: "8px", marginBottom: "8px"}}
                                    >
                                        {/* MMID field */}
                                        <Form.Item
                                            {...field}
                                            name={[field.name, "mmid"]}
                                            rules={[
                                                {required: true, message: "MMID required"},
                                                {
                                                    pattern: /^\d{8}$/,
                                                    message: "MMID must be exactly 8 digits",
                                                },
                                            ]}
                                            style={{flex: 1}}
                                        >
                                            <Input placeholder="MMID" maxLength={8}/>
                                        </Form.Item>

                                        {/* Theo field */}
                                        <Form.Item
                                            {...field}
                                            name={[field.name, "theo"]}
                                            rules={[{required: true, message: "Theo required"}]}
                                            style={{flex: 2}}
                                        >
                                            <InputNumber
                                                placeholder="Theo"
                                                style={{width: "100%"}}
                                                precision={0}
                                                min={0}
                                                step={1}
                                                parser={(value: string | undefined) => {
                                                    if (!value) return 0;
                                                    const cleaned = value.split(".")[0].replace(/\D/g, "");
                                                    return cleaned ? parseInt(cleaned, 10) : 0;
                                                }}
                                                formatter={(value) =>
                                                    value
                                                        ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                                        : ""
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                ))}
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </Card>
    );

};

export default TopPlayers;
